import { useState as useReactState } from 'react';
import { AlertTriangle, Download, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { useDaily, useEpochs, useIncome, useRisk, useState } from '../hooks/useApi';
import type { DailyNavRow, IncomeSummary, RiskMetrics, State } from '../lib/api';
import { Button } from '../components/ui/button';
import { SectionHeader } from '../components/ui/section-header';
import { MetricCard } from '../components/ui/metric-card';
import { DriftAreaChart } from '../components/charts/drift-area-chart';
import { PriceLineChart } from '../components/charts/price-line-chart';
import { NavAreaChart } from '../components/charts/nav-area-chart';
import { PagedActionsTable } from '../components/paged-actions-table';
import { OperationsPanel } from '../components/operations-panel';
import { DeploymentProgressPanel } from '../components/deployment-progress-panel';
import { LpEpochsTable } from '../components/lp-epochs-table';
import {
  cn,
  formatBps,
  formatCurrency,
  formatDuration,
  formatEth,
  formatPercent,
} from '../lib/utils';

const RANGE_OPTIONS = [
  { label: '24h', hours: 24 },
  { label: '7d', hours: 24 * 7 },
  { label: '30d', hours: 24 * 30 },
  { label: 'All', hours: 24 * 365 * 5 }, // effectively "everything"
] as const;

// TEMP (operator request): suppress the degraded-state banner on the
// frontend while stale post-incident alarms ("frequent failures", "low gas")
// age out. The bot still computes + logs health and alerts to Telegram —
// this only hides the dashboard banner. Flip back to true to re-enable.
const SHOW_HEALTH_BANNER = false;

export default function Overview() {
  const { data: state, error: stateError, isLoading } = useState();
  const { data: income } = useIncome();
  const { data: daily } = useDaily(30);
  // Page-0 epochs — same query key as LpEpochsTable's first page, so
  // React Query serves both consumers from a single request. Used for
  // the real-bounds range bar below.
  const { data: epochRows } = useEpochs(state?.strategy?.id ?? null, 11, 0);
  const { data: risk } = useRisk(30);
  const [navHours, setNavHours] = useReactState<number>(24 * 7);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (stateError || !state) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-negative">
        Couldn&apos;t reach the bot API.
      </div>
    );
  }

  const snap = state.latest;
  const ethPrice = snap?.ethPrice ?? null;
  const lpEth = snap?.lpEth ?? null;
  const lpUsdc = snap?.lpUsdc ?? null;
  const lpValue =
    lpEth !== null && lpUsdc !== null && ethPrice !== null
      ? lpEth * ethPrice + lpUsdc
      : null;
  const short = snap?.perpShortEth ?? null;
  const shortNotional = short !== null && ethPrice !== null ? short * ethPrice : null;
  const netEth = lpEth !== null && short !== null ? lpEth - short : null;
  const netUsd = netEth !== null && ethPrice !== null ? netEth * ethPrice : null;

  // ── Cross-venue basis (this tick) ──
  // The LP is valued at the DEX pool price (ethPrice); the HL short is marked
  // at HL's mark price. The same token prices differently across venues
  // moment-to-moment, so a non-zero basis makes a delta-neutral pair look
  // briefly skewed. basisImpact ≈ the dollar gap that introduces on the matched
  // hedge notional — i.e. how much of the LP-vs-HL P&L difference is just venue
  // basis (transient, converges) rather than a real hedging problem.
  const poolPx = ethPrice;
  const hlMark = snap?.hlMarkPrice ?? null;
  const basisAbs = poolPx !== null && hlMark !== null ? poolPx - hlMark : null;
  const basisBps =
    poolPx !== null && hlMark !== null && hlMark !== 0
      ? (poolPx / hlMark - 1) * 10_000
      : null;
  const basisImpact = basisAbs !== null && short !== null ? short * basisAbs : null;
  const drift = snap?.driftBps ?? null;
  const driftAbsBelowThreshold =
    drift !== null && Math.abs(drift) < state.thresholds.driftBps;

  const totalActivity =
    state.counts.autoHedgeCount24h +
    state.counts.autoHarvestCount24h +
    state.counts.autoRedeployCount24h;

  // ── NAV / growth ──
  // Phase 3 (revised): strategy NAV = total bot NAV. Growth is anchored
  // to deployedCapitalUsd. Phase 4 will add attribution to decompose
  // growth into fees / gas / HODL drift / hedge PnL.
  const strategyNav = state.strategy?.currentNavUsd ?? null;
  // Volatile-side label — 'ETH' for the legacy strategies, 'VIRTUAL' etc.
  // for other venues. Falls back to ETH for old payloads.
  const baseSym = state.strategy?.baseSymbol ?? 'ETH';
  const deployedCapital = state.strategy?.deployedCapitalUsd ?? null;
  const budgetUsdc = state.strategy?.budgetUsdc ?? null;
  const strategyGrowth = state.strategy?.growthUsd ?? null;
  const strategyGrowthPct = state.strategy?.growthPct ?? null;
  const inceptionTs = state.inception?.ts;
  const daysRunning = inceptionTs ? (Date.now() - inceptionTs) / (1000 * 60 * 60 * 24) : null;

  // ── Lifetime LP APR (averaged across days with measurable accrual) ──
  // Fee-only APR = trading fees / LP-value-at-start × 365.
  // Total APR = (fees + AERO emissions) / LP-value-at-start × 365.
  // We drop days where a mid-day claim invalidated the simple-delta math
  // (those rows arrive as `null` from the API).
  const feeAprRows = (daily ?? []).filter((d) => d.lpFeeApr !== null);
  const totalAprRows = (daily ?? []).filter((d) => d.lpTotalApr !== null);
  const lifetimeFeeApr =
    feeAprRows.length > 0
      ? feeAprRows.reduce((s, d) => s + (d.lpFeeApr ?? 0), 0) / feeAprRows.length
      : null;
  const lifetimeTotalApr =
    totalAprRows.length > 0
      ? totalAprRows.reduce((s, d) => s + (d.lpTotalApr ?? 0), 0) /
        totalAprRows.length
      : null;

  // Range viz — REAL band prices from the open epoch's minted ticks
  // (price = 1.0001^tick × 1e12 for the 18/6-decimals pools). Falls back
  // to the ±LP_RANGE_PCT estimate only when no open epoch exists. The
  // epochs query matches LpEpochsTable's page-0 key, so React Query
  // serves both from one request.
  const openEpochForRange = (epochRows ?? []).find((e) => e.status === 'open');
  const tickToPrice = (tick: number): number => Math.pow(1.0001, tick) * 1e12;
  const center = ethPrice ?? 0;
  const halfWidth = state.thresholds.lpRangePct / 100;
  // Guard against degenerate bounds: epochs created by the redeploy path
  // before the tick-recording fix carry 0/0 (and 1.0001^0 × 1e12 renders
  // a $1T band). Real WETH/USDC ticks are large negatives; requiring
  // upper > lower AND nonzero filters the legacy rows to the estimate.
  const hasRealRange =
    openEpochForRange !== undefined &&
    Number.isFinite(openEpochForRange.mintTickLower) &&
    Number.isFinite(openEpochForRange.mintTickUpper) &&
    openEpochForRange.mintTickUpper > openEpochForRange.mintTickLower &&
    !(openEpochForRange.mintTickLower === 0 && openEpochForRange.mintTickUpper === 0);
  const lower = hasRealRange
    ? tickToPrice(openEpochForRange!.mintTickLower)
    : center * (1 - halfWidth);
  const upper = hasRealRange
    ? tickToPrice(openEpochForRange!.mintTickUpper)
    : center * (1 + halfWidth);
  const rangePct =
    upper > lower && center > 0 ? ((center - lower) / (upper - lower)) * 100 : 50;
  const clampedPct = Math.max(0, Math.min(100, rangePct));
  const distFromMid = Math.abs(clampedPct - 50);
  const rangeMarkerClass =
    distFromMid < 15 ? 'bg-positive' : distFromMid < 35 ? 'bg-warning' : 'bg-negative';

  return (
    <div className="space-y-6 max-w-7xl">
      <SectionHeader
        title="Overview"
        subtitle="Strategy performance and current position state"
        size="lg"
        actions={
          <>
            <Button variant="outline" size="sm" disabled>
              <Download className="h-3.5 w-3.5" />
              Export
            </Button>
            <Button variant="outline" size="sm" disabled>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          </>
        }
      />

      {/* ── Degraded-state banner — TEMPORARILY HIDDEN (operator request) ──
             The bot still computes + logs health; we're only suppressing the
             frontend banner while stale post-incident "frequent failures" /
             "low gas" alarms age out. Re-enable by flipping SHOW_HEALTH_BANNER
             back to true once the 24h failure window has cleared. ── */}
      {SHOW_HEALTH_BANNER && state.health && state.health.state === 'degraded' && (
        <HealthBanner
          reasons={state.health.reasons}
          since={state.health.since}
        />
      )}

      {/* ── Deployment progress (Path 2). Renders nothing unless this
             strategy has an active deployment. Polls every 2s while live. ── */}
      <DeploymentProgressPanel strategyId={state.strategy?.id ?? null} />

      {/* ── NAV / growth strip — strategy NAV vs deployed capital ── */}
      <NavMetricsBar
        strategyNav={strategyNav}
        deployedCapital={deployedCapital}
        budgetUsdc={budgetUsdc}
        strategyGrowth={strategyGrowth}
        strategyGrowthPct={strategyGrowthPct}
        daysRunning={daysRunning}
      />

      {/* ── Strategy NAV chart ── */}
      <div className="rounded-lg border border-border bg-surface p-6">
        <SectionHeader
          title="Strategy NAV over time"
          subtitle="LP + pending fees + pending AERO + HL perp + HL spot. Excludes idle wallet residuals + gas."
          size="sm"
          className="mb-4"
          actions={
            <div className="inline-flex rounded-md border border-border bg-surface overflow-hidden">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setNavHours(opt.hours)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium transition-colors',
                    'border-r border-border last:border-r-0',
                    navHours === opt.hours
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          }
        />
        <NavAreaChart baselineUsd={deployedCapital} baselineLabel="deployed" hours={navHours} height={260} />
      </div>

      {/* ── Current state KPI grid ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <MetricCard label={`${baseSym} price`} value={formatCurrency(ethPrice)} />
        <MetricCard label="LP value" value={formatCurrency(lpValue)} />
        <MetricCard
          label="Hedge short"
          value={short !== null ? `${formatEth(short)} ${baseSym}` : '—'}
        />
        <MetricCard
          label="Drift"
          value={formatBps(drift)}
          trend={driftAbsBelowThreshold ? 'neutral' : 'down'}
        />
        <MetricCard label="Margin used" value={formatCurrency(snap?.perpMargin ?? null)} />
        <MetricCard label="Activity 24h" value={`${totalActivity} actions`} />
        <MetricCard
          label="Pending fees"
          value={formatCurrency(snap?.lpFeesPendingUsd ?? null)}
        />
        <MetricCard
          label="HL total"
          value={formatCurrency(snap?.hlSpotUsdc ?? null)}
        />
        <MetricCard
          label="Gas spent (total)"
          value={formatCurrency(income?.gasCostUsd ?? null)}
        />
        {state.strategy?.venue !== 'uniswap_v3' && (
          <MetricCard
            label="AERO pending"
            value={formatCurrency(state.strategy?.aeroPendingUsd ?? null)}
            trend={
              (state.strategy?.aeroPendingUsd ?? 0) > 0 ? 'up' : 'neutral'
            }
          />
        )}
        <MetricCard
          label="HL unrealized"
          value={formatCurrency(state.strategy?.hlUnrealizedPnl ?? null)}
          trend={
            (state.strategy?.hlUnrealizedPnl ?? 0) > 0
              ? 'up'
              : (state.strategy?.hlUnrealizedPnl ?? 0) < 0
              ? 'down'
              : 'neutral'
          }
        />
        <MetricCard
          label={`LP fee APR (${feeAprRows.length}d avg)`}
          value={lifetimeFeeApr !== null ? `${lifetimeFeeApr.toFixed(1)}%` : '—'}
          trend={
            lifetimeFeeApr !== null && lifetimeFeeApr > 0 ? 'up' : 'neutral'
          }
        />
        <MetricCard
          label={
            state.strategy?.venue === 'uniswap_v3'
              ? 'LP total APR (fees)'
              : 'LP total APR (fees + AERO)'
          }
          value={
            lifetimeTotalApr !== null ? `${lifetimeTotalApr.toFixed(1)}%` : '—'
          }
          trend={
            lifetimeTotalApr !== null && lifetimeTotalApr > 0 ? 'up' : 'neutral'
          }
        />
      </div>

      {/* ── Context strip ── */}
      <div className="rounded-lg border border-border bg-surface px-6 py-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div>
          <span className="text-muted-foreground">LP composition</span>
          <p className="font-medium tabular-nums mt-0.5">
            {lpEth !== null ? formatEth(lpEth) : '—'} {baseSym} + {formatCurrency(lpUsdc)} USDC
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Short notional</span>
          <p className="font-medium tabular-nums mt-0.5">
            {shortNotional !== null ? formatCurrency(shortNotional) : '—'}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Net delta</span>
          <p className="font-medium tabular-nums mt-0.5">
            {netEth !== null ? formatEth(netEth) : '—'} {baseSym} ({formatCurrency(netUsd)})
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">24h breakdown</span>
          <p className="font-medium tabular-nums mt-0.5">
            {state.counts.autoHedgeCount24h} hedge · {state.counts.autoHarvestCount24h} harvest ·{' '}
            {state.counts.autoRedeployCount24h} redeploy
            {state.counts.actionFailures24h > 0 && (
              <span className="text-negative"> · {state.counts.actionFailures24h} failed</span>
            )}
          </p>
        </div>
      </div>

      {/* ── Cross-venue basis: pool price vs HL mark at this tick ── */}
      <div className="rounded-lg border border-border bg-surface px-6 py-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div>
          <span className="text-muted-foreground">Pool price ({baseSym}/USDC)</span>
          <p className="font-medium tabular-nums mt-0.5">
            {poolPx !== null ? `$${poolPx.toFixed(4)}` : '—'}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">HL mark price</span>
          <p className="font-medium tabular-nums mt-0.5">
            {hlMark !== null ? `$${hlMark.toFixed(4)}` : '—'}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Basis (pool − mark)</span>
          <p
            className={cn(
              'font-medium tabular-nums mt-0.5',
              basisBps !== null && Math.abs(basisBps) >= 1
                ? basisBps > 0
                  ? 'text-positive'
                  : 'text-negative'
                : '',
            )}
          >
            {basisBps !== null
              ? `${basisBps > 0 ? '+' : ''}${basisBps.toFixed(1)} bps`
              : '—'}
            {basisAbs !== null ? ` ($${basisAbs.toFixed(4)})` : ''}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">≈ basis on hedge</span>
          <p className="font-medium tabular-nums mt-0.5">
            {basisImpact !== null ? formatCurrency(basisImpact) : '—'}
            <span className="text-muted-foreground"> (transient)</span>
          </p>
        </div>
      </div>

      {/* ── Economics breakdown + Daily growth (side by side on wide screens) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PositionEconomicsCard income={income} state={state} />
        <DailyGrowthTable rows={daily ?? []} />
      </div>

      {/* ── LP Epochs history (Path 2) — every LP NFT this strategy has held ── */}
      <LpEpochsTable strategyId={state.strategy?.id ?? null} />

      {/* ── Risk + IL panel ── */}
      <RiskPanel
        risk={risk ?? null}
        ilUsd={state.strategy?.ilUsd ?? null}
        ilPct={state.strategy?.ilPct ?? null}
        hodlValueUsd={state.strategy?.hodlValueUsd ?? null}
      />

      {/* ── Drift + price charts ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-border bg-surface p-6">
          <SectionHeader title="Drift — 24h (bps)" size="sm" className="mb-4" />
          <DriftAreaChart driftThresholdBps={state.thresholds.driftBps} />
        </div>
        <div className="rounded-lg border border-border bg-surface p-6">
          <SectionHeader title={`${baseSym} price — 24h`} size="sm" className="mb-4" />
          <PriceLineChart coinLabel={baseSym} />
        </div>
      </div>

      {/* ── Range visualization ── */}
      <div className="rounded-lg border border-border bg-surface p-6">
        <SectionHeader
          title="Position range"
          subtitle={
            hasRealRange
              ? `Actual minted band (ticks ${openEpochForRange!.mintTickLower} … ${openEpochForRange!.mintTickUpper}) — marker = current price.`
              : `Estimated from current price ± ${state.thresholds.lpRangePct}% (no open position).`
          }
          size="sm"
          className="mb-4"
        />
        <div className="space-y-3">
          <div className="flex justify-between text-xs text-muted-foreground tabular-nums">
            <span>{formatCurrency(lower)}</span>
            <span>price ≈ {formatCurrency(center)}</span>
            <span>{formatCurrency(upper)}</span>
          </div>
          <div className="relative h-3 rounded-full bg-muted overflow-hidden">
            <div className="absolute top-0 bottom-0 w-0.5 bg-border" style={{ left: '50%' }} />
            <div
              className={`absolute top-0 bottom-0 w-1.5 -ml-0.5 ${rangeMarkerClass} transition-all duration-500`}
              style={{ left: `${clampedPct}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>lower band</span>
            <span>center</span>
            <span>upper band</span>
          </div>
        </div>
      </div>

      {/* ── Manual operations (Phase 5/6) ── */}
      <OperationsPanel strategy={state.strategy} />

      {/* ── Recent actions — paginated + filterable ── */}
      <div className="space-y-3">
        <SectionHeader
          title="Recent actions"
          subtitle="Audit trail from the actions table — filter by kind, status, or date"
          size="sm"
        />
        <PagedActionsTable />
      </div>
    </div>
  );
}

// ─── HealthBanner — surfaces bot:health when state=degraded ───
const REASON_LABELS: Record<string, { title: string; detail: string }> = {
  naked_short: {
    title: 'Naked HL short',
    detail: 'A perp short is open with no LP visible. Exposure is unhedged.',
  },
  unhedged_lp: {
    title: 'Unhedged LP',
    detail: 'LP is open with no matching perp short. Auto-hedge has not (yet) acted.',
  },
  low_gas: {
    title: 'Low gas balance',
    detail: 'Wallet ETH is below the safe gas floor. Future on-chain actions may fail.',
  },
  stuck_tx: {
    title: 'Stuck transaction',
    detail: 'A recent write surfaced a nonce/mempool conflict. New writes may be blocked until it clears.',
  },
  frequent_failures: {
    title: 'Frequent action failures',
    detail: 'Multiple confirmed-failed actions in the last 24h. Inspect Actions for the error trail.',
  },
};

function HealthBanner({ reasons, since }: { reasons: string[]; since: number | null }) {
  return (
    <div className="rounded-lg border border-negative/40 bg-negative/10 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-negative flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-negative">
              Bot is degraded
            </p>
            {since && (
              <p className="text-xs text-muted-foreground tabular-nums">
                since {new Date(since).toLocaleString()}
              </p>
            )}
          </div>
          <ul className="mt-2 space-y-1">
            {reasons.map((r) => {
              const meta = REASON_LABELS[r] ?? { title: r, detail: '' };
              return (
                <li key={r} className="text-sm text-foreground">
                  <span className="font-medium">{meta.title}</span>
                  {meta.detail && (
                    <span className="text-muted-foreground"> — {meta.detail}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─── PositionEconomicsCard — three-block decomposition ───────────────
//
// Replaces the older IncomePanel which mixed income (positive
// inflows) with position MTM (signed, can be negative) and costs
// (always negative). Operator needed to see those separately so
// the reconciliation math is visible:
//
//   Net = Income + Position changes − Costs
//
// vs the existing Growth (NAV-based) view, the gap between these
// two should be the deployment gas (paid pre-Starting-NAV, so
// excluded from Growth but counted as a cost here).
//
// HL realized PnL comes from the epoch's TRACKED realized (sum of
// auto_hedge close/resize realizedPnl attributions) — 0 until a close is
// recorded. We deliberately do NOT derive it as a balancing item off the
// total HL change: that double-counts the open short's unrealized loss
// (it's baked into HL account value) and shows a spurious "realized" sitting
// right on top of the unrealized. A freshly-opened hedge has realized = 0 by
// definition. Full per-close tracking lands with #86; until then this
// under-counts rather than fabricates.
function PositionEconomicsCard({
  income,
  state,
}: {
  income: IncomeSummary | undefined;
  state: State;
}) {
  // Pull the open epoch — needed for mintTotalUsd, currentLpValueUsd,
  // hlFeesUsd, hlFundingUsd. The component shows "no data" until the
  // first observation tick after deployment.
  const { data: epochs } = useEpochs(state.strategy?.id ?? null);
  const openEpoch = (epochs ?? []).find((e) => e.status === 'open');
  // LP value at the INITIAL deploy = the OLDEST epoch's mint. deployedCapital is
  // fixed at that first deploy (initial LP + initial HL), so the LP/HL baseline
  // split MUST use the initial LP. Using the open epoch's mint is wrong after a
  // redeploy: the LP size changes, and the cross-epoch LP delta then gets
  // mis-assigned to the HL baseline — which made HL P&L show a phantom loss.
  const firstEpoch = (epochs ?? []).slice().sort((a, b) => a.mintTs - b.mintTs)[0];
  const lpAtDeploy = firstEpoch?.mintTotalUsd ?? openEpoch?.mintTotalUsd ?? 0;

  // ── Inputs ──────────────────────────────────────────────────────
  // GROWTH is the single source of truth: strategy NAV now − NAV at
  // deploy. Everything below EXPLAINS it; nothing recomputes it. Each
  // leg is a balance delta, so LP + HL + residual always equals Growth.
  const growthUsd = state.strategy?.growthUsd ?? null;
  const deployedCapital = state.strategy?.deployedCapitalUsd ?? null;

  const mintTotal = openEpoch?.mintTotalUsd ?? 0;
  const currentLpValue = openEpoch?.currentLpValueUsd ?? 0;
  const aeroClaimedUsd = income?.aeroSwapUsd ?? 0;
  const aeroPendingUsd = state.strategy?.aeroPendingUsd ?? 0;
  const lpFeesPending = income?.pendingSwapFeesUsd ?? 0;
  // HL realized/funding/fees summed across ALL epochs since deploy — a prior
  // redeploy's hedge-reset realized lives on the CLOSED epoch, not the open one,
  // so summing is what makes this attribution tie to the HL balance delta
  // instead of dumping the cross-epoch realized into "unreconciled".
  const allEpochs = epochs ?? [];
  const hlFunding =
    allEpochs.length > 0
      ? allEpochs.reduce((s, e) => s + (e.hlFundingUsd ?? 0), 0)
      : (income?.fundingUsd ?? 0);
  const hlUnrealized = state.strategy?.hlUnrealizedPnl ?? 0;
  const hlRealized = allEpochs.reduce((s, e) => s + (e.hlRealizedPnlUsd ?? 0), 0);
  const hlFees = allEpochs.reduce((s, e) => s + (e.hlFeesUsd ?? 0), 0);
  const deploymentGas = income?.gasCostUsd ?? 0;

  // Per-leg P&L as BALANCE DELTAS (deploy → now). These sum to Growth by
  // construction, so the card can never disagree with the NAV tile:
  //   LP leg = (LP token value + pending fees + AERO) now − LP value at mint
  //   HL leg = (HL account value + HL spot USDC) now − HL value at deploy
  // HL-at-deploy = baseline NAV − LP-at-mint (the only split we store).
  // HL leg = spot USDC TOTAL only. HL marks spot `total` to include the open
  // perp's unrealized PnL, so it IS the full HL account equity (proven vs the
  // HL ledger to <$0.02). Do NOT add hlAccountValue — it's a sub-view of this
  // same spot money; adding it double-counted ~$13, and the old usable+
  // accountValue under-counted by ~the unrealized PnL (the drift you saw).
  const hlNow = state.latest?.hlSpotUsdc ?? 0;
  const hlBaseline =
    deployedCapital !== null ? deployedCapital - lpAtDeploy : null;
  const hasData =
    openEpoch !== undefined &&
    mintTotal > 0 &&
    growthUsd !== null &&
    hlBaseline !== null;

  const lpPnl =
    currentLpValue + lpFeesPending + aeroPendingUsd + aeroClaimedUsd - lpAtDeploy;
  const hlPnl = hlBaseline !== null ? hlNow - hlBaseline : 0;
  // Cumulative redeploy/deploy remainders refunded to the wallet, added back
  // into NAV (migration 0021). Counted as its own leg so it isn't buried in
  // "unattributed."
  const dustUsd = state.strategy?.dustUsd ?? 0;
  // Residual: Growth minus the legs we can name. ~0 normally (just
  // snapshot-source rounding); larger means a leg moved in a way we can't tie
  // out.
  const unattributed = (growthUsd ?? 0) - (lpPnl + hlPnl + dustUsd);

  // "Of which" for the HL balance delta. HL P&L = spot_total_now −
  // spot_total_at_deploy, which decomposes EXACTLY into:
  //   unrealized (live MTM, already inside spot_total)
  //   + realized cash since deploy (= Σrealized − Σfees + Σfunding)
  // We source the cash pieces from recorded actions; whatever they don't
  // explain is a real deposit/withdrawal (normally 0), NOT a price-driven
  // plug. This line no longer drifts with the market.
  const hlAttributed = hlUnrealized + hlRealized + hlFunding - hlFees;
  const hlUnexplained = hlPnl - hlAttributed;

  const trendOf = (v: number): 'positive' | 'negative' | 'neutral' =>
    v > 0.005 ? 'positive' : v < -0.005 ? 'negative' : 'neutral';

  return (
    <div className="rounded-lg border border-border bg-surface p-6 space-y-5">
      <SectionHeader
        title="Strategy economics"
        subtitle="Growth, decomposed into LP and HL legs — every line ties to NAV"
        size="sm"
      />

      {!hasData ? (
        <p className="text-sm text-muted-foreground">
          Awaiting the first post-deploy snapshot…
        </p>
      ) : (
        <>
          {/* ── Growth headline — the single source of truth ── */}
          <div className="rounded-md border border-primary/40 bg-primary/5 p-4">
            <div className="flex items-baseline justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Growth
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Strategy NAV now − NAV at deploy
                </p>
              </div>
              <p
                className={cn(
                  'text-2xl font-bold tabular-nums',
                  trendOf(growthUsd!) === 'positive' && 'text-positive',
                  trendOf(growthUsd!) === 'negative' && 'text-negative',
                  trendOf(growthUsd!) === 'neutral' && 'text-foreground',
                )}
              >
                {formatCurrency(growthUsd!)}
              </p>
            </div>
          </div>

          {/* ── Breakdown by leg — sums to Growth by construction ── */}
          <EconBlock
            title="Breakdown by leg"
            subtitle="Balance deltas since deploy — these sum to Growth"
            total={growthUsd!}
            totalTrend={trendOf(growthUsd!)}
          >
            <EconRow
              label="LP P&L"
              value={lpPnl}
              trend={trendOf(lpPnl)}
              hint={`token ${formatCurrency(currentLpValue - lpAtDeploy)} + fees ${formatCurrency(lpFeesPending)}${
                aeroPendingUsd + aeroClaimedUsd > 0
                  ? ` + AERO ${formatCurrency(aeroPendingUsd + aeroClaimedUsd)}`
                  : ''
              }`}
            />
            <EconRow
              label="HL P&L"
              value={hlPnl}
              trend={trendOf(hlPnl)}
              hint={`spot equity ${formatCurrency(hlBaseline!)} → ${formatCurrency(hlNow)}`}
            />
            <EconRow
              label="Dust (recovered residual)"
              value={dustUsd}
              trend={trendOf(dustUsd)}
              hint="Un-deployed redeploy remainders, kept in NAV"
            />
            <EconRow
              label="Unattributed"
              value={unattributed}
              trend={trendOf(unattributed)}
              hint="Growth − (LP + HL + Dust); snapshot-source rounding"
            />
          </EconBlock>

          {/* ── HL P&L attribution (best-effort) ── */}
          <EconBlock
            title="HL P&L — of which"
            subtitle="Attribution of the perp balance change"
            total={hlPnl}
            totalTrend={trendOf(hlPnl)}
          >
            <EconRow
              label="Unrealized (open short)"
              value={hlUnrealized}
              trend={trendOf(hlUnrealized)}
              hint="Live MTM of the open perp"
            />
            <EconRow
              label="Realized"
              value={hlRealized}
              trend={trendOf(hlRealized)}
              hint="Closes / resizes since deploy"
            />
            <EconRow
              label="Funding"
              value={hlFunding}
              trend={trendOf(hlFunding)}
              hint={hlFunding >= 0 ? 'Received on short' : 'Paid'}
            />
            <EconRow
              label="Trading fees"
              value={-hlFees}
              trend={hlFees > 0 ? 'negative' : 'neutral'}
              hint="Taker fee × notional"
            />
            <EconRow
              label="Net transfers / unreconciled"
              value={hlUnexplained}
              trend={trendOf(hlUnexplained)}
              hint="Spot-cash move not yet in recorded actions — real deposits/withdrawals, else ~0"
            />
          </EconBlock>

          {/* ── Footnote: deployment gas sits outside strategy NAV ── */}
          <p className="text-xs text-muted-foreground leading-relaxed">
            Deployment gas {formatCurrency(deploymentGas)} was paid from the gas
            wallet, outside strategy NAV — it does not affect Growth.
          </p>
        </>
      )}
    </div>
  );
}

function EconBlock({
  title,
  subtitle,
  total,
  totalTrend,
  children,
}: {
  title: string;
  subtitle: string;
  total: number;
  totalTrend: 'positive' | 'negative' | 'neutral';
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
            {title}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        <p
          className={cn(
            'text-sm font-bold tabular-nums',
            totalTrend === 'positive' && 'text-positive',
            totalTrend === 'negative' && 'text-negative',
            totalTrend === 'neutral' && 'text-foreground',
          )}
        >
          {total >= 0 ? '+' : ''}
          {formatCurrency(total)}
        </p>
      </div>
      <div className="space-y-1.5 pl-3 border-l border-border">{children}</div>
    </div>
  );
}

function EconRow({
  label,
  value,
  trend,
  hint,
}: {
  label: string;
  value: number;
  trend: 'positive' | 'negative' | 'neutral';
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-xs">
      <div className="min-w-0 flex-1">
        <p className="text-foreground">{label}</p>
        {hint && <p className="text-muted-foreground text-[10.5px] mt-0.5">{hint}</p>}
      </div>
      <p
        className={cn(
          'tabular-nums font-medium shrink-0',
          trend === 'positive' && 'text-positive',
          trend === 'negative' && 'text-negative',
          trend === 'neutral' && 'text-muted-foreground',
        )}
      >
        {value >= 0 && trend !== 'neutral' ? '+' : ''}
        {formatCurrency(value)}
      </p>
    </div>
  );
}

// ─── DailyGrowthTable — per-UTC-day NAV first/last/delta ───
function DailyGrowthTable({ rows }: { rows: DailyNavRow[] }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-6 flex flex-col">
      <SectionHeader
        title="Daily NAV (UTC)"
        subtitle="Start-of-day vs end-of-day NAV per UTC calendar day"
        size="sm"
        className="mb-4"
      />
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No daily data yet — wait for first UTC midnight to pass.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide">
                <th className="font-medium pb-2 pr-4">Date</th>
                <th className="font-medium pb-2 pr-4 text-right">Start</th>
                <th className="font-medium pb-2 pr-4 text-right">End</th>
                <th className="font-medium pb-2 pr-4 text-right">Δ USD</th>
                <th className="font-medium pb-2 pr-4 text-right">Δ %</th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="Trading-fee APR = (swap fees accrued / LP value at start) × 365. ~0 for staked Aerodrome positions — they earn AERO instead (see AERO APR)."
                >
                  Fee APR
                </th>
                <th
                  className="font-medium pb-2 pr-4 text-right"
                  title="AERO-emissions APR = (AERO accrued / LP value at start) × 365. The real LP yield for staked Aerodrome positions; ~0 for Uniswap."
                >
                  AERO APR
                </th>
                <th
                  className="font-medium pb-2 text-right"
                  title="Total APR (fees + AERO emissions) — comparable to Aerodrome UI's headline APR"
                >
                  Total APR
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 14).map((r) => {
                const trend =
                  r.changeUsd === null
                    ? 'neutral'
                    : r.changeUsd > 0
                    ? 'positive'
                    : r.changeUsd < 0
                    ? 'negative'
                    : 'neutral';
                return (
                  <tr key={r.date} className="border-t border-border">
                    <td className="py-2 pr-4 tabular-nums">{r.date}</td>
                    <td className="py-2 pr-4 tabular-nums text-right text-muted-foreground">
                      {formatCurrency(r.firstNavUsd)}
                    </td>
                    <td className="py-2 pr-4 tabular-nums text-right">
                      {formatCurrency(r.lastNavUsd)}
                    </td>
                    <td
                      className={cn(
                        'py-2 pr-4 tabular-nums text-right',
                        trend === 'positive' && 'text-positive',
                        trend === 'negative' && 'text-negative',
                      )}
                    >
                      {r.changeUsd !== null ? formatCurrency(r.changeUsd) : '—'}
                    </td>
                    <td
                      className={cn(
                        'py-2 pr-4 tabular-nums text-right',
                        trend === 'positive' && 'text-positive',
                        trend === 'negative' && 'text-negative',
                      )}
                    >
                      {r.changePct !== null ? formatPercent(r.changePct) : '—'}
                    </td>
                    <td
                      className="py-2 pr-4 tabular-nums text-right text-muted-foreground"
                      title={
                        r.feesAccruedUsd !== null && r.lpValueStartUsd !== null
                          ? `${formatCurrency(r.feesAccruedUsd)} fees / ${formatCurrency(r.lpValueStartUsd)} LP value`
                          : 'No clean signal (mid-day claim or insufficient data)'
                      }
                    >
                      {r.lpFeeApr !== null ? `${r.lpFeeApr.toFixed(1)}%` : '—'}
                    </td>
                    <td
                      className="py-2 pr-4 tabular-nums text-right text-muted-foreground"
                      title={
                        r.aeroAccruedUsd !== null && r.lpValueStartUsd !== null
                          ? `${formatCurrency(r.aeroAccruedUsd)} AERO / ${formatCurrency(r.lpValueStartUsd)} LP value`
                          : 'No clean signal (mid-day claim or insufficient data)'
                      }
                    >
                      {r.lpAeroApr !== null ? `${r.lpAeroApr.toFixed(1)}%` : '—'}
                    </td>
                    <td
                      className="py-2 tabular-nums text-right"
                      title={
                        r.lpTotalApr !== null && r.lpValueStartUsd !== null
                          ? `Fees ${formatCurrency(r.feesAccruedUsd)} + AERO ${formatCurrency(r.aeroAccruedUsd)} / LP ${formatCurrency(r.lpValueStartUsd)}`
                          : 'No clean signal (mid-day claim or insufficient data)'
                      }
                    >
                      {r.lpTotalApr !== null
                        ? `${r.lpTotalApr.toFixed(1)}%`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── RiskPanel — drawdown, vol, IL ───
function RiskPanel({
  risk,
  ilUsd,
  ilPct,
  hodlValueUsd,
}: {
  risk: RiskMetrics | null;
  ilUsd: number | null;
  ilPct: number | null;
  hodlValueUsd: number | null;
}) {
  const ilTrend: 'positive' | 'negative' | 'neutral' =
    ilUsd == null || ilUsd === 0 ? 'neutral' : ilUsd > 0 ? 'positive' : 'negative';

  return (
    <div className="rounded-lg border border-border bg-surface p-6">
      <SectionHeader
        title="Risk & IL"
        subtitle="LP vs HODL impermanent loss, plus drawdown/volatility over the last 30 days"
        size="sm"
        className="mb-4"
      />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <RiskCell
          label="LP vs HODL"
          value={formatCurrency(ilUsd)}
          sub={
            ilPct != null
              ? `${ilPct > 0 ? '+' : ''}${ilPct.toFixed(2)}% vs HODL`
              : 'No baseline yet'
          }
          trend={ilTrend}
        />
        <RiskCell
          label="HODL value"
          value={formatCurrency(hodlValueUsd)}
          sub="If you'd just held deposit tokens"
        />
        <RiskCell
          label="Max drawdown"
          value={formatCurrency(risk?.maxDrawdownUsd ?? null)}
          sub={
            risk?.maxDrawdownPct != null
              ? `${risk.maxDrawdownPct.toFixed(2)}% from peak`
              : undefined
          }
          trend={risk?.maxDrawdownUsd && risk.maxDrawdownUsd < 0 ? 'negative' : 'neutral'}
        />
        <RiskCell
          label="Annualized vol"
          value={risk?.annualVol != null ? `${risk.annualVol.toFixed(2)}%` : '—'}
          sub={
            risk?.dailyVol != null ? `${risk.dailyVol.toFixed(2)}% daily` : undefined
          }
        />
        <RiskCell
          label="Sharpe (no rf)"
          value={risk?.sharpe != null ? risk.sharpe.toFixed(2) : '—'}
          sub={risk?.daySamples ? `${risk.daySamples} day samples` : undefined}
          trend={
            risk?.sharpe == null
              ? 'neutral'
              : risk.sharpe > 1
              ? 'positive'
              : risk.sharpe < 0
              ? 'negative'
              : 'neutral'
          }
        />
      </div>
    </div>
  );
}

function RiskCell({
  label,
  value,
  sub,
  trend = 'neutral',
}: {
  label: string;
  value: string;
  sub?: string;
  trend?: 'positive' | 'negative' | 'neutral';
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <p
        className={cn(
          'text-lg font-bold tabular-nums mt-0.5',
          trend === 'positive' && 'text-positive',
          trend === 'negative' && 'text-negative',
          trend === 'neutral' && 'text-foreground',
        )}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── NavMetricsBar (strategy NAV vs deployed capital) ───
//
// Phase 3 (revised): strategy NAV = total bot NAV. Growth measured
// against the strategy's deployedCapitalUsd (the user's declared
// commitment when they funded the strategy).
//
// Phase 4 will add an attribution panel below that decomposes the
// growth number into its components (fees earned, gas paid, HODL
// drift on residuals, hedge PnL). That's where you'll see WHY growth
// is what it is.
interface NavMetricsBarProps {
  strategyNav: number | null;
  deployedCapital: number | null;
  budgetUsdc: number | null;
  strategyGrowth: number | null;
  strategyGrowthPct: number | null;
  daysRunning: number | null;
}

function NavMetricsBar({
  strategyNav,
  deployedCapital,
  budgetUsdc,
  strategyGrowth,
  strategyGrowthPct,
  daysRunning,
}: NavMetricsBarProps) {
  const growthTrend: 'positive' | 'negative' | 'neutral' =
    strategyGrowth === null || strategyGrowth === 0
      ? 'neutral'
      : strategyGrowth > 0
      ? 'positive'
      : 'negative';

  const TrendIcon = growthTrend === 'positive' ? TrendingUp : TrendingDown;

  return (
    <div className="rounded-lg border border-border bg-surface flex items-stretch divide-x divide-border overflow-x-auto">
      <Cell label="Strategy NAV" value={formatCurrency(strategyNav)} />
      <Cell
        label="Growth"
        value={formatCurrency(strategyGrowth)}
        sub={
          strategyGrowthPct !== null ? (
            <span
              className={cn(
                'inline-flex items-center gap-1 tabular-nums font-medium',
                growthTrend === 'positive' && 'text-positive',
                growthTrend === 'negative' && 'text-negative',
                growthTrend === 'neutral' && 'text-muted-foreground',
              )}
            >
              {growthTrend !== 'neutral' && <TrendIcon className="h-3 w-3" />}
              {formatPercent(strategyGrowthPct)} vs deployed
            </span>
          ) : undefined
        }
      />
      <Cell
        label="Starting NAV"
        value={formatCurrency(deployedCapital)}
        sub={
          budgetUsdc !== null ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              budget {formatCurrency(budgetUsdc)}
            </span>
          ) : undefined
        }
      />
      <Cell
        label="Days running"
        value={daysRunning !== null ? daysRunning.toFixed(1) : '—'}
        sub={daysRunning !== null ? formatDuration(daysRunning * 24 * 3600 * 1000) : undefined}
      />
    </div>
  );
}

function Cell({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className="py-5 px-6 flex-shrink-0 flex flex-col gap-0.5 min-w-[180px]">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </p>
      <p className="text-xl font-bold tabular-nums text-foreground">{value}</p>
      {sub && <div className="text-xs">{sub}</div>}
    </div>
  );
}
